"use client"
import { memo, useCallback, useEffect, useLayoutEffect, useRef } from "react"
import { animate, useMotionValue } from "motion/react"
import { useAnimationTuning } from "@/app/playbook/animation/animationTuningContext"
import { resolveEasingBezierArray } from "@/app/playbook/animation/animationTuningDefaults"
import { type LaborCategoryKey } from "../../shared/laborCategories"
import type { LaborEconomicValueSegment } from "./useLaborEconomicValueSegments"
import {
  resolveSizing,
  squareBorderRadiusForSize,
  squareCountsForSegments,
  squaresPerRow,
  totalValueForSegments,
} from "./groupedSquaresMath"

type GroupedSquaresCanvasProps = {
  segments: LaborEconomicValueSegment[]
  dollarsPerSquareInitial: number
  startSquareSize: number
  minSquareSize: number
  squareGap: number
  maxWidth: number
  maxHeight: number
  // When set, every square keeps its square shape but is filled with a
  // halftone dot-screen in this single color (segment colors are ignored),
  // matching the extrapolated graph's highest-risk dither. Off (solid) when
  // undefined.
  squareDitherColor?: string
  // Tile size (px) of the halftone dot grid. Smaller = finer dither. Defaults
  // to the shared 4px tile.
  squareDitherCellPx?: number
  // Fraction [0,1] of each tile covered by its dot. Higher = denser/darker.
  squareDitherDotDensity?: number
  // Per-segment dot density, overriding squareDitherDotDensity for that
  // segment's squares (keyed by segment key, e.g. "humans" / "aiCog" /
  // "robotPhys"). Lets each labor group dither at its own density.
  squareDitherDotDensityByKey?: Partial<Record<LaborCategoryKey | "humans", number>>
  // When set together with squareDitherColor, every square also gets an outline
  // (stroke) in this color on top of the dither fill — the "dithered + black
  // outline" lower-section look. Only applies in dither mode.
  squareDitherOutlineColor?: string
  // Stroke width (px) for squareDitherOutlineColor. Defaults to 1.
  squareDitherOutlineWidthPx?: number
  // When set (and squareDitherColor is unset), every square is filled solid
  // with this single color instead of dithered or per-segment colors.
  squareSolidColor?: string
  // When set (and dither/solid are unset), every square is drawn as an outline
  // (stroke) in this single color instead of filled — matching the lower
  // section's "outline only" fill mode. Per-type colors are ignored.
  squareOutlineColor?: string
  // Stroke width (px) for the outline mode.
  squareOutlineWidthPx?: number
  // Per-segment outline override: the listed segment keys are drawn as an
  // outline (stroke) in their given color/width instead of filled, while every
  // other segment keeps its normal per-type fill. Lets one group read as an
  // outline and another as a solid fill in the same chart (e.g. HUD 34: human
  // labor outlined, AI agents filled). Ignored when a global dither/solid/
  // outline mode above is active. Optional `radiusPx` overrides the default
  // per-size corner radius so the outline can match another block's rounding
  // (e.g. the distribution-of-power cells' 2px radius).
  squareOutlineByKey?: Partial<Record<LaborCategoryKey | "humans", { color: string; widthPx: number; radiusPx?: number }>>
  // Per-segment FILL color override, painted in place of the segment's own
  // color. Resolved into state without touching the count/height tweens, so
  // a hover can swap it live (see the labor dots-invert hover) — pass stable
  // identities per state, like squareOutlineByKey.
  squareFillColorByKey?: Partial<Record<LaborCategoryKey | "humans", string>>
  // Overrides the corner radius (px) for FILLED squares (per-type fills and the
  // squareSolidColor fill), so the filled boxes can match another block's
  // rounding instead of the per-size default (which is only ~1px at the small
  // labor-square sizes). Used by HUD 34 so the solid-black AI squares round to
  // the same 2px as the outlined human squares beside them. Outline-mode and
  // dither strokes keep their own radius handling.
  squareFillRadiusPx?: number
  // When "dot", every unit is drawn as a filled circle inscribed in its cell
  // (diameter = the cell/square size) instead of a square. Set the cell size to
  // the wanted dot diameter via startSquareSize/minSquareSize. Defaults to
  // "square".
  squareShape?: "square" | "dot"
  // Per-segment diameter scale for dot mode: the listed keys' dots are drawn at
  // cell size × this factor (e.g. { aiCog: 1.1 } makes the AI-agent dots 10%
  // bigger). The grid cell itself keeps its size, so the packing is unchanged —
  // the scaled dot just fills more (or spills slightly out of) its cell.
  dotDiameterScaleByKey?: Partial<Record<LaborCategoryKey | "humans", number>>
  // Stretches each cell's WIDTH to squareSize * squareWidthScale (its height
  // stays squareSize), turning the squares into rectangles. 1 = square, 2 =
  // twice as long. Fewer cells fit per row, so the grid grows taller.
  squareWidthScale?: number
  // Guarantees the listed segment keys at least this many squares (taken from
  // the largest other segment, so the total is unchanged). Keeps a tiny group
  // (e.g. the first AI agents at the scenario start) visible instead of
  // rounding away to zero squares.
  minSquareCountByKey?: Partial<Record<LaborCategoryKey | "humans", number>>
  // The container width is rarely an exact multiple of the cell pitch, so a
  // row's last cell normally stops up to a pitch short of the right edge. When
  // set, the leftover is spread evenly into the horizontal gaps so a full
  // row's last cell lands flush on the container's right edge (cells keep
  // their size; columns stay aligned across rows).
  justifyRows?: boolean
}

// Halftone dither matched to the extrapolated graph's highest-risk band:
// circle dots on a 4px tile, radius = tile * sqrt(maxDitherDensity / PI) with
// maxDitherDensity = 0.59, i.e. ~1.73px on the default tile. Drawn in CSS px
// (the canvas context is scaled by devicePixelRatio).
const DEFAULT_DITHER_TILE_PX = 4
const DEFAULT_DITHER_DOT_AREA_FRACTION = 0.59

// Dot radius that fills `areaFraction` of a `tilePx`-sized tile, so the
// dot-screen density holds as the cell size changes.
const dotRadiusForTile = (tilePx: number, areaFraction: number): number =>
  tilePx * Math.sqrt(areaFraction / Math.PI)

type SegmentKey = LaborCategoryKey | "humans"

type ResolvedSegment = {
  key: SegmentKey
  color: [number, number, number, number]
  value: number
}

type Slice = {
  key: SegmentKey
  color: [number, number, number, number]
  count: number
}

// Draws the grouped square field while keeping animation state outside React.
export const GroupedSquaresCanvas = memo(({
  segments,
  dollarsPerSquareInitial,
  startSquareSize,
  minSquareSize,
  squareGap,
  maxWidth,
  maxHeight,
  squareDitherColor,
  squareDitherCellPx = DEFAULT_DITHER_TILE_PX,
  squareDitherDotDensity = DEFAULT_DITHER_DOT_AREA_FRACTION,
  squareDitherDotDensityByKey,
  squareDitherOutlineColor,
  squareDitherOutlineWidthPx = 1,
  squareSolidColor,
  squareOutlineColor,
  squareOutlineWidthPx = 1,
  squareOutlineByKey,
  squareFillColorByKey,
  squareFillRadiusPx,
  squareShape = "square",
  dotDiameterScaleByKey,
  squareWidthScale = 1,
  minSquareCountByKey,
  justifyRows = false,
}: GroupedSquaresCanvasProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const countMotion = useMotionValue(0)
  const heightMotion = useMotionValue(0)
  // Live-tunable dot-field growth (see animationTuningContext / the studio's
  // "Labor graph" section). Read through refs so the animate() calls below —
  // set up in a [segments]-keyed effect — pick up the latest values when they
  // fire, without restarting the tween on every unrelated render.
  const tuning = useAnimationTuning()
  const growthMsRef = useRef(tuning.laborDotGrowthMs)
  growthMsRef.current = tuning.laborDotGrowthMs
  const heightMsRef = useRef(tuning.laborDotHeightMs)
  heightMsRef.current = tuning.laborDotHeightMs
  const dotEaseRef = useRef<[number, number, number, number]>(
    resolveEasingBezierArray(tuning.laborDotEasing),
  )
  dotEaseRef.current = resolveEasingBezierArray(tuning.laborDotEasing)
  const stateRef = useRef({
    resolvedSegments: [] as ResolvedSegment[],
    totalValue: 0,
    targetCount: 0,
    squareSize: startSquareSize,
    gap: squareGap,
    containerWidth: 0,
    devicePixelRatio: 1,
    rafId: null as number | null,
    lastCanvasHeight: 0,
    squareDitherColor: null as [number, number, number, number] | null,
    squareDitherCellPx: DEFAULT_DITHER_TILE_PX,
    squareDitherDotDensity: DEFAULT_DITHER_DOT_AREA_FRACTION,
    squareDitherDotDensityByKey: null as Partial<Record<SegmentKey, number>> | null,
    squareDitherOutlineColor: null as [number, number, number, number] | null,
    squareDitherOutlineWidthPx: 1,
    squareSolidColor: null as [number, number, number, number] | null,
    squareOutlineColor: null as [number, number, number, number] | null,
    squareOutlineWidthPx: 1,
    squareOutlineByKey: null as Partial<Record<SegmentKey, { color: [number, number, number, number]; widthPx: number; radiusPx?: number }>> | null,
    squareFillColorByKey: null as Partial<Record<SegmentKey, [number, number, number, number]>> | null,
    squareFillRadiusPx: null as number | null,
    minSquareCountByKey: null as Partial<Record<SegmentKey, number>> | null,
    dotMode: false,
    dotDiameterScaleByKey: null as Partial<Record<SegmentKey, number>> | null,
    // Seed from the prop so the mount-time sizing pass (a layout effect that
    // runs before the widthScale setter effect below) already lays out wide.
    widthScale: squareWidthScale,
    justifyRows,
  })
  const resolvedColorCacheRef = useRef(new Map<string, [number, number, number, number]>())

  // Cached per raw color string: getComputedStyle forces a style recalc, and
  // this runs for every segment on every animation frame during scrolling.
  const resolveColor = useCallback((rawColor: string): [number, number, number, number] => {
    const cached = resolvedColorCacheRef.current.get(rawColor)
    if (cached) return cached
    const canvas = canvasRef.current
    if (!canvas) return [0, 0, 0, 1]
    const prev = canvas.style.color
    canvas.style.color = rawColor
    const resolved = getComputedStyle(canvas).color || rawColor
    canvas.style.color = prev
    const parsed = parseRgba(resolved)
    resolvedColorCacheRef.current.set(rawColor, parsed)
    return parsed
  }, [])

  // Resolve the optional single fill colors once (not per frame), so the
  // paint path can read them straight off state.
  useEffect(() => {
    stateRef.current.squareDitherColor = squareDitherColor
      ? resolveColor(squareDitherColor)
      : null
  }, [squareDitherColor, resolveColor])

  useEffect(() => {
    stateRef.current.squareSolidColor = squareSolidColor
      ? resolveColor(squareSolidColor)
      : null
  }, [squareSolidColor, resolveColor])

  useEffect(() => {
    stateRef.current.squareOutlineColor = squareOutlineColor
      ? resolveColor(squareOutlineColor)
      : null
  }, [squareOutlineColor, resolveColor])

  useEffect(() => {
    stateRef.current.squareOutlineWidthPx = squareOutlineWidthPx
  }, [squareOutlineWidthPx])

  useEffect(() => {
    stateRef.current.squareFillRadiusPx = squareFillRadiusPx ?? null
  }, [squareFillRadiusPx])

  useEffect(() => {
    stateRef.current.dotMode = squareShape === "dot"
  }, [squareShape])

  useEffect(() => {
    stateRef.current.dotDiameterScaleByKey = dotDiameterScaleByKey ?? null
  }, [dotDiameterScaleByKey])

  useEffect(() => {
    stateRef.current.minSquareCountByKey = minSquareCountByKey ?? null
    ensureRafRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minSquareCountByKey])

  useEffect(() => {
    stateRef.current.justifyRows = justifyRows
    ensureRafRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justifyRows])

  // Resolve the per-segment outline override's colors once (not per frame).
  useEffect(() => {
    if (!squareOutlineByKey) {
      stateRef.current.squareOutlineByKey = null
      return
    }
    const resolved: Partial<Record<SegmentKey, { color: [number, number, number, number]; widthPx: number; radiusPx?: number }>> = {}
    for (const [key, style] of Object.entries(squareOutlineByKey)) {
      if (!style) continue
      resolved[key as SegmentKey] = { color: resolveColor(style.color), widthPx: style.widthPx, radiusPx: style.radiusPx }
    }
    stateRef.current.squareOutlineByKey = resolved
    // A hover can swap the outline map live — repaint even when the count/
    // height tweens are idle.
    ensureRafRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squareOutlineByKey, resolveColor])

  // Resolve the per-segment fill override's colors once (not per frame) —
  // tween-free, same contract as the outline map above.
  useEffect(() => {
    if (!squareFillColorByKey) {
      stateRef.current.squareFillColorByKey = null
    } else {
      const resolved: Partial<Record<SegmentKey, [number, number, number, number]>> = {}
      for (const [key, color] of Object.entries(squareFillColorByKey)) {
        if (!color) continue
        resolved[key as SegmentKey] = resolveColor(color)
      }
      stateRef.current.squareFillColorByKey = resolved
    }
    ensureRafRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squareFillColorByKey, resolveColor])

  useEffect(() => {
    stateRef.current.squareDitherCellPx = squareDitherCellPx
  }, [squareDitherCellPx])

  useEffect(() => {
    stateRef.current.squareDitherDotDensity = squareDitherDotDensity
  }, [squareDitherDotDensity])

  useEffect(() => {
    stateRef.current.squareDitherDotDensityByKey = squareDitherDotDensityByKey ?? null
  }, [squareDitherDotDensityByKey])

  useEffect(() => {
    stateRef.current.squareDitherOutlineColor = squareDitherOutlineColor
      ? resolveColor(squareDitherOutlineColor)
      : null
  }, [squareDitherOutlineColor, resolveColor])

  useEffect(() => {
    stateRef.current.squareDitherOutlineWidthPx = squareDitherOutlineWidthPx
  }, [squareDitherOutlineWidthPx])

  const draw = useCallback(() => {
    const state = stateRef.current
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) {
      state.rafId = null
      return
    }
    const animatedCount = Math.max(0, Math.round(countMotion.get()))
    // The count and height tweens run on different clocks (500ms vs 350ms,
    // both restarted every frame while the scroll-driven year animates), so
    // the animated height can lag the height the animated count actually
    // needs — slicing the bottom dot row at the canvas edge. Never size the
    // canvas below the painted content; the height tween remains the smooth
    // floor.
    const heightForAnimatedCount = heightForCount(
      animatedCount, state.containerWidth, state.squareSize, state.gap, state.widthScale,
    )
    const animatedHeight = Math.max(0, heightMotion.get(), heightForAnimatedCount)
    const slices = slicesForResolvedSegments(state.resolvedSegments, animatedCount, state.minSquareCountByKey)
    prepareCanvasForFrame(canvas, state, animatedHeight)
    paintSlices(ctx, state, slices)
    if (countMotion.isAnimating() || heightMotion.isAnimating()) {
      state.rafId = requestAnimationFrame(draw)
    } else {
      state.rafId = null
    }
  }, [countMotion, heightMotion])

  const ensureRafRunning = useCallback(() => {
    const state = stateRef.current
    if (state.rafId !== null) return
    state.rafId = requestAnimationFrame(draw)
  }, [draw])

  // The fill-color / dither effects above only mutate stateRef; they don't
  // repaint. Schedule a redraw when any live-tunable fill input changes so the
  // canvas reflects ⌘+G edits immediately (otherwise it stays stale until the
  // next resize or count animation).
  useEffect(() => {
    ensureRafRunning()
  }, [
    squareDitherColor,
    squareSolidColor,
    squareDitherCellPx,
    squareDitherDotDensity,
    squareDitherDotDensityByKey,
    squareDitherOutlineColor,
    squareDitherOutlineWidthPx,
    squareOutlineColor,
    squareOutlineWidthPx,
    squareOutlineByKey,
    squareFillRadiusPx,
    squareShape,
    dotDiameterScaleByKey,
    ensureRafRunning,
  ])

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const state = stateRef.current
    const measured = Math.min(maxWidth, container.clientWidth || maxWidth)
    applySegmentsToState(state, segments, measured, {
      maxHeight, startSquareSize, minSquareSize, squareGap, dollarsPerSquareInitial, resolveColor,
    })
    countMotion.set(state.targetCount)
    heightMotion.set(heightForCount(state.targetCount, measured, state.squareSize, squareGap, state.widthScale))
    ensureRafRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-resolve sizing when the height budget changes (the parent's viewport
  // cap settling after mount, or a window resize). Sizing is stateless, so
  // this snaps straight to the right size for the new budget (matching the
  // width-resize path). Runs pre-paint so the post-mount cap correction never
  // flashes an overflowing grid.
  useLayoutEffect(() => {
    const state = stateRef.current
    if (state.containerWidth <= 0) return
    applySegmentsToState(state, segments, state.containerWidth, {
      maxHeight, startSquareSize, minSquareSize, squareGap, dollarsPerSquareInitial, resolveColor,
    })
    countMotion.stop()
    heightMotion.stop()
    countMotion.set(state.targetCount)
    heightMotion.set(
      heightForCount(state.targetCount, state.containerWidth, state.squareSize, squareGap, state.widthScale)
    )
    ensureRafRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxHeight])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? container.clientWidth
      const clamped = Math.min(maxWidth, w)
      const state = stateRef.current
      if (Math.abs(state.containerWidth - clamped) < 0.5) return
      state.containerWidth = clamped
      heightMotion.set(heightForCount(state.targetCount, clamped, state.squareSize, state.gap, state.widthScale))
      ensureRafRunning()
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [ensureRafRunning, maxWidth, heightMotion])

  useEffect(() => {
    stateRef.current.gap = squareGap
    ensureRafRunning()
  }, [squareGap, ensureRafRunning])

  // Repaint when the devicePixelRatio changes (browser zoom, dragging the
  // window to another monitor): the container's width may not change, so the
  // ResizeObserver never fires and an idle canvas would keep its old-ratio
  // backing (blurry). The media query matches only the CURRENT ratio, so it
  // has to be re-registered after every change.
  useEffect(() => {
    let mql: MediaQueryList | null = null
    const onRatioChange = () => {
      ensureRafRunning()
      subscribe()
    }
    const subscribe = () => {
      mql?.removeEventListener("change", onRatioChange)
      mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
      mql.addEventListener("change", onRatioChange)
    }
    subscribe()
    return () => mql?.removeEventListener("change", onRatioChange)
  }, [ensureRafRunning])

  // Re-resolve sizing/layout when the cell width scale changes at runtime
  // (⌘+G tuning) — the mount pass already seeds it, but later edits need the
  // grid to re-flow to the new cell width.
  useEffect(() => {
    const state = stateRef.current
    state.widthScale = squareWidthScale
    if (state.containerWidth <= 0) return
    applySegmentsToState(state, segments, state.containerWidth, {
      maxHeight, startSquareSize, minSquareSize, squareGap, dollarsPerSquareInitial, resolveColor,
    })
    countMotion.set(state.targetCount)
    heightMotion.set(heightForCount(state.targetCount, state.containerWidth, state.squareSize, squareGap, state.widthScale))
    ensureRafRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squareWidthScale])

  useEffect(() => {
    const state = stateRef.current
    if (state.containerWidth <= 0) return
    applySegmentsToState(state, segments, state.containerWidth, {
      maxHeight, startSquareSize, minSquareSize, squareGap, dollarsPerSquareInitial, resolveColor,
    })
    const targetHeight = heightForCount(state.targetCount, state.containerWidth, state.squareSize, squareGap, state.widthScale)
    const countControls = animate(countMotion, state.targetCount, tweenOptions(ensureRafRunning, growthMsRef.current, dotEaseRef.current))
    const heightControls = animate(heightMotion, targetHeight, tweenOptions(ensureRafRunning, heightMsRef.current, dotEaseRef.current))
    ensureRafRunning()
    return () => {
      countControls.stop()
      heightControls.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments])

  useEffect(() => {
    const state = stateRef.current
    return () => {
      if (state.rafId !== null) {
        cancelAnimationFrame(state.rafId)
        state.rafId = null
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden"
      style={{ maxWidth, maxHeight }}
    >
      {/* No CSS width here: prepareCanvasForFrame sets an explicit style
          width/height that mirrors the backing store exactly, so the bitmap
          is never rescaled (a `width: 100%` against a rounded backing blurs
          the dots by a subpixel resample). */}
      <canvas ref={canvasRef} style={{ display: "block" }} />
    </div>
  )
})
GroupedSquaresCanvas.displayName = "GroupedSquaresCanvas"

const slicesForResolvedSegments = (
  segments: ResolvedSegment[],
  count: number,
  minSquareCountByKey: Partial<Record<SegmentKey, number>> | null,
): Slice[] => {
  const squareCounts = squareCountsForSegments(segments, count, minSquareCountByKey ?? undefined)
  return segments.map((segment, index) => ({
    key: segment.key,
    color: segment.color,
    count: squareCounts[index] ?? 0,
  }))
}

// The canvas backing store grows in coarse buckets and never shrinks. The
// height tween used to reallocate the backing at a new unique size nearly
// every animation frame while the grid grew — and macOS pools IOSurfaces per
// size, so each unique height stranded another set of GPU surfaces (a large
// share of the tab's GPU-memory footprint over a reading session). The
// container div owns the visible (animated) height and clips the bucket
// slack, so layout and visuals are unchanged.
const CANVAS_HEIGHT_BUCKET_PX = 256

const prepareCanvasForFrame = (
  canvas: HTMLCanvasElement,
  state: ReturnType<typeof createStateReader>,
  animatedHeight: number,
) => {
  // Re-read the ratio every frame (cheap): it changes with browser zoom and
  // when the window moves between monitors, and painting at a stale ratio
  // renders the whole grid at the wrong resolution (blurry) from then on.
  const dpr = window.devicePixelRatio || 1
  state.devicePixelRatio = dpr
  // Size the backing from the UNFLOORED container width. The canvas gets that
  // exact size back as its CSS width below, so CSS px × ratio equals the
  // backing on both axes — any mismatch (the old `width: 100%` against a
  // floored backing) makes the browser rescale the bitmap by a hair, blurring
  // every dot even on displays where text/SVG rasterize sharp.
  const targetPxWidth = Math.max(1, Math.ceil(state.containerWidth * dpr))
  // The container clips at this height. Leave one device pixel of slack below
  // the content: the bottom dot row is drawn tangent to the content height,
  // and at fractional ratios (125%/150% displays, page zoom) the container's
  // own height snaps to the device grid — without slack that snap shaves the
  // antialiased bottom edge off the last row of dots.
  const targetCssHeight = animatedHeight <= 0 ? 0 : (Math.ceil(animatedHeight * dpr) + 1) / dpr
  const neededPxHeight = Math.max(1, Math.ceil(targetCssHeight * dpr))
  const bucketedPxHeight =
    Math.ceil(neededPxHeight / CANVAS_HEIGHT_BUCKET_PX) * CANVAS_HEIGHT_BUCKET_PX
  if (canvas.width !== targetPxWidth || canvas.height < bucketedPxHeight) {
    canvas.width = targetPxWidth
    canvas.height = bucketedPxHeight
    // CSS size mirrors the backing exactly (no buffer scaling); the
    // container's clip hides everything beyond the visible box.
    canvas.style.width = `${targetPxWidth / dpr}px`
    canvas.style.height = `${bucketedPxHeight / dpr}px`
  }
  const container = canvas.parentElement
  if (
    container &&
    (state.lastCanvasHeight !== targetCssHeight || container.style.height === "")
  ) {
    container.style.height = `${targetCssHeight}px`
    state.lastCanvasHeight = targetCssHeight
  }
}

// Horizontal distance between cell starts. Normally the natural pitch
// (cell width + gap); with justifyRows the per-row leftover (the container
// width is rarely an exact pitch multiple) is spread evenly into the gaps so
// a full row's last cell ends flush at the container's right edge. Applied to
// every row, so columns stay aligned between full and partial rows.
const horizontalSlot = (
  state: ReturnType<typeof createStateReader>,
  perRow: number,
): number => {
  const cellW = state.squareSize * state.widthScale
  if (state.justifyRows && perRow > 1) {
    return (state.containerWidth - cellW) / (perRow - 1)
  }
  return cellW + state.gap
}

const paintSlices = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slices: Slice[],
) => {
  // The VISIBLE box (the container's clip box); the halftone painter needs
  // it, while the clear must cover the whole bucketed backing.
  const targetCssWidth = Math.max(0, Math.ceil(state.containerWidth))
  const targetCssHeight = Math.max(0, Math.ceil(state.lastCanvasHeight))
  ctx.setTransform(state.devicePixelRatio, 0, 0, state.devicePixelRatio, 0, 0)
  ctx.clearRect(
    0, 0,
    ctx.canvas.width / state.devicePixelRatio,
    ctx.canvas.height / state.devicePixelRatio,
  )
  const perRow = squaresPerRow(state.containerWidth, state.squareSize, state.gap, state.widthScale)
  if (perRow <= 0 || state.squareSize <= 0) return
  if (state.squareSolidColor) {
    paintSolidSquares(ctx, state, slices, perRow)
    return
  }
  if (state.squareDitherColor) {
    paintHalftoneSquares(ctx, state, slices, perRow, targetCssWidth, targetCssHeight)
    return
  }
  if (state.squareOutlineColor) {
    paintOutlineSquares(ctx, state, slices, perRow)
    return
  }
  if (state.squareOutlineByKey) {
    paintMixedSlices(ctx, state, slices, perRow, state.squareOutlineByKey)
    return
  }
  paintVisibleSlices(ctx, state, slices, perRow)
}

// Like paintVisibleSlices, but any segment listed in outlineByKey is stroked as
// an outline (in its own color/width) instead of filled, while the rest keep
// their normal per-type fill. Lets one group read as an outline and another as
// a solid fill in the same chart (HUD 34: humans outlined, AI filled).
const paintMixedSlices = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slices: Slice[],
  perRow: number,
  outlineByKey: Partial<Record<SegmentKey, { color: [number, number, number, number]; widthPx: number; radiusPx?: number }>>,
) => {
  let drawn = 0
  for (const slice of slices) {
    if (slice.count <= 0) continue
    const outline = outlineByKey[slice.key]
    if (outline) {
      paintOutlineSlice(ctx, state, slice, drawn, perRow, outline)
    } else {
      paintSlice(ctx, state, slice, drawn, perRow)
    }
    drawn += slice.count
  }
}

// Strokes one contiguous run of squares as outlines (no fill), insetting the
// stroke by half its width so it stays within the square bounds — the per-slice
// version of paintOutlineSquares.
const paintOutlineSlice = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slice: Slice,
  startIndex: number,
  perRow: number,
  outline: { color: [number, number, number, number]; widthPx: number; radiusPx?: number },
) => {
  const width = outline.widthPx
  const inset = width / 2
  const cellW = state.squareSize * state.widthScale
  const cellH = state.squareSize
  const sideW = Math.max(0, cellW - width)
  const sideH = Math.max(0, cellH - width)
  // Match another block's corner rounding when radiusPx is given (e.g. the
  // distribution-of-power cells' 2px radius), else fall back to the per-size
  // default. Clamp to half the shorter side so the radius never exceeds the box.
  const radius = Math.min(
    outline.radiusPx ?? squareBorderRadiusForSize(state.squareSize),
    sideW / 2,
    sideH / 2,
  )
  const slotX = horizontalSlot(state, perRow)
  const slotY = cellH + state.gap
  ctx.strokeStyle = `rgba(${outline.color[0]}, ${outline.color[1]}, ${outline.color[2]}, ${outline.color[3]})`
  ctx.lineWidth = width
  for (let i = 0; i < slice.count; i++) {
    const idx = startIndex + i
    const x = (idx % perRow) * slotX + inset
    const y = Math.floor(idx / perRow) * slotY + inset
    ctx.beginPath()
    if (state.dotMode) {
      // Stroked circle/ellipse inscribed in the cell — the outlined-dot
      // counterpart to the filled dot in paintSquare (so an outlined segment
      // reads as a hollow ring, not a square, in dot mode).
      ctx.ellipse(x + sideW / 2, y + sideH / 2, sideW / 2, sideH / 2, 0, 0, Math.PI * 2)
    } else if (radius > 0) {
      ctx.roundRect(x, y, sideW, sideH, radius)
    } else {
      ctx.rect(x, y, sideW, sideH)
    }
    ctx.stroke()
  }
}

const paintVisibleSlices = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slices: Slice[],
  perRow: number,
) => {
  let drawn = 0
  for (const slice of slices) {
    if (slice.count <= 0) continue
    paintSlice(ctx, state, slice, drawn, perRow)
    drawn += slice.count
  }
}

const paintSlice = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slice: Slice,
  startIndex: number,
  perRow: number,
) => {
  const fill = state.squareFillColorByKey?.[slice.key] ?? slice.color
  ctx.fillStyle = `rgba(${fill[0]}, ${fill[1]}, ${fill[2]}, ${fill[3]})`
  const dotScale = state.dotDiameterScaleByKey?.[slice.key] ?? 1
  for (let i = 0; i < slice.count; i++) {
    paintSquare(ctx, state, startIndex + i, perRow, dotScale)
  }
}

const paintSquare = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  idx: number,
  perRow: number,
  dotScale = 1,
) => {
  // Cells are squareSize tall; widthScale stretches them wider (rectangles).
  const cellW = state.squareSize * state.widthScale
  const cellH = state.squareSize
  const x = (idx % perRow) * horizontalSlot(state, perRow)
  const y = Math.floor(idx / perRow) * (cellH + state.gap)
  // Dot shape: draw a filled ellipse inscribed in the cell, so the labor units
  // read as dots (circles when the cell is square, ovals when stretched).
  // dotScale grows/shrinks the ellipse around the cell center without moving
  // the grid (per-segment override via dotDiameterScaleByKey).
  if (state.dotMode) {
    ctx.beginPath()
    ctx.ellipse(x + cellW / 2, y + cellH / 2, (cellW / 2) * dotScale, (cellH / 2) * dotScale, 0, 0, Math.PI * 2)
    ctx.fill()
    return
  }
  // Filled cells round to squareFillRadiusPx when the caller sets it (HUD 34's
  // solid-black AI squares match the 2px outlined human squares), else the
  // per-size default. Clamp to half the shorter side so the radius fits.
  const radius = Math.min(
    state.squareFillRadiusPx ?? squareBorderRadiusForSize(state.squareSize),
    cellW / 2,
    cellH / 2,
  )
  if (radius > 0) {
    ctx.beginPath()
    ctx.roundRect(x, y, cellW, cellH, radius)
    ctx.fill()
  } else {
    ctx.fillRect(x, y, cellW, cellH)
  }
}

// Keeps every square as a square, but fills the squares with a halftone
// dot-screen in one shared color (the inter-square gaps stay empty). Each
// segment is painted separately so it can carry its own dot density via
// squareDitherDotDensityByKey; segments without an override fall back to the
// shared squareDitherDotDensity. The per-type color distinction is dropped —
// only the density varies between groups.
const paintHalftoneSquares = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slices: Slice[],
  perRow: number,
  cssWidth: number,
  cssHeight: number,
) => {
  const color = state.squareDitherColor
  if (!color) return
  let startIndex = 0
  for (const slice of slices) {
    if (slice.count <= 0) continue
    const density =
      state.squareDitherDotDensityByKey?.[slice.key] ?? state.squareDitherDotDensity
    paintHalftoneForSquareRange(ctx, state, color, density, startIndex, slice.count, perRow, cssWidth, cssHeight)
    startIndex += slice.count
  }
  // Optional black outline on top of the dither fill (HUD 35).
  const outline = state.squareDitherOutlineColor
  if (outline) {
    const total = slices.reduce((sum, slice) => sum + slice.count, 0)
    strokeSquareRange(ctx, state, outline, state.squareDitherOutlineWidthPx, 0, total, perRow)
  }
}

// Strokes a contiguous run of squares as outlines (inset by half the stroke
// width so the border stays within the square bounds). Shared by the dither +
// outline overlay and the outline-only paint paths.
const strokeSquareRange = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  color: [number, number, number, number],
  width: number,
  startIndex: number,
  count: number,
  perRow: number,
) => {
  const inset = width / 2
  const cellW = state.squareSize * state.widthScale
  const cellH = state.squareSize
  const sideW = Math.max(0, cellW - width)
  const sideH = Math.max(0, cellH - width)
  const radius = Math.min(squareBorderRadiusForSize(state.squareSize), sideW / 2, sideH / 2)
  const slotX = horizontalSlot(state, perRow)
  const slotY = cellH + state.gap
  ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
  ctx.lineWidth = width
  for (let i = startIndex; i < startIndex + count; i++) {
    const x = (i % perRow) * slotX + inset
    const y = Math.floor(i / perRow) * slotY + inset
    ctx.beginPath()
    if (radius > 0) {
      ctx.roundRect(x, y, sideW, sideH, radius)
    } else {
      ctx.rect(x, y, sideW, sideH)
    }
    ctx.stroke()
  }
}

// Paints the halftone dot grid clipped to one contiguous run of squares
// (indices [startIndex, startIndex + count)). The dot grid is laid across the
// whole field so adjacent same-density runs stay phase-aligned; the clip keeps
// each run's dots inside its own squares.
const paintHalftoneForSquareRange = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  color: [number, number, number, number],
  density: number,
  startIndex: number,
  count: number,
  perRow: number,
  cssWidth: number,
  cssHeight: number,
) => {
  const cellW = state.squareSize * state.widthScale
  const cellH = state.squareSize
  const slotX = horizontalSlot(state, perRow)
  const slotY = cellH + state.gap
  const radius = Math.min(squareBorderRadiusForSize(state.squareSize), cellW / 2, cellH / 2)

  ctx.save()
  ctx.beginPath()
  for (let i = startIndex; i < startIndex + count; i++) {
    const x = (i % perRow) * slotX
    const y = Math.floor(i / perRow) * slotY
    if (radius > 0) {
      ctx.roundRect(x, y, cellW, cellH, radius)
    } else {
      ctx.rect(x, y, cellW, cellH)
    }
  }
  ctx.clip()

  const tile = state.squareDitherCellPx
  const dotRadius = dotRadiusForTile(tile, density)
  ctx.fillStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
  for (let cy = tile / 2; cy < cssHeight + tile; cy += tile) {
    for (let cx = tile / 2; cx < cssWidth + tile; cx += tile) {
      ctx.beginPath()
      ctx.arc(cx, cy, dotRadius, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  ctx.restore()
}

// Fills every square solid with one shared color (like paintHalftoneSquares but
// without the dot-screen), dropping the per-type color distinction.
const paintSolidSquares = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slices: Slice[],
  perRow: number,
) => {
  const color = state.squareSolidColor
  if (!color) return
  const total = slices.reduce((sum, slice) => sum + slice.count, 0)
  ctx.fillStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
  for (let i = 0; i < total; i++) {
    paintSquare(ctx, state, i, perRow)
  }
}

// Draws every square as an outline (stroke) in one shared color instead of a
// fill — the labor-mix equivalent of the lower section's "outline only" mode.
// The stroke is inset by half its width so it stays within the square bounds
// and adjacent squares' borders don't overlap.
const paintOutlineSquares = (
  ctx: CanvasRenderingContext2D,
  state: ReturnType<typeof createStateReader>,
  slices: Slice[],
  perRow: number,
) => {
  const color = state.squareOutlineColor
  if (!color) return
  const total = slices.reduce((sum, slice) => sum + slice.count, 0)
  const width = state.squareOutlineWidthPx
  const inset = width / 2
  const cellW = state.squareSize * state.widthScale
  const cellH = state.squareSize
  const sideW = Math.max(0, cellW - width)
  const sideH = Math.max(0, cellH - width)
  const radius = Math.min(squareBorderRadiusForSize(state.squareSize), sideW / 2, sideH / 2)
  const slotX = horizontalSlot(state, perRow)
  const slotY = cellH + state.gap
  ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
  ctx.lineWidth = width
  for (let i = 0; i < total; i++) {
    const x = (i % perRow) * slotX + inset
    const y = Math.floor(i / perRow) * slotY + inset
    ctx.beginPath()
    if (radius > 0) {
      ctx.roundRect(x, y, sideW, sideH, radius)
    } else {
      ctx.rect(x, y, sideW, sideH)
    }
    ctx.stroke()
  }
}

const applySegmentsToState = (
  state: ReturnType<typeof createStateReader>,
  segments: LaborEconomicValueSegment[],
  measuredWidth: number,
  options: {
    maxHeight: number
    startSquareSize: number
    minSquareSize: number
    squareGap: number
    dollarsPerSquareInitial: number
    resolveColor: (rawColor: string) => [number, number, number, number]
  },
) => {
  const totalValue = totalValueForSegments(segments)
  const sizing = resolveSizing(totalValue, measuredWidth, options.maxHeight, options.startSquareSize,
    options.minSquareSize, options.squareGap, options.dollarsPerSquareInitial, state.widthScale)
  state.containerWidth = measuredWidth
  state.squareSize = sizing.squareSize
  state.totalValue = totalValue
  state.resolvedSegments = resolveSegmentsForCanvas(segments, options.resolveColor)
  state.targetCount = totalValue > 0 && sizing.dollarsPerSquare > 0 ? Math.ceil(totalValue / sizing.dollarsPerSquare) : 0
}

const resolveSegmentsForCanvas = (
  segments: LaborEconomicValueSegment[],
  resolveColor: (rawColor: string) => [number, number, number, number],
): ResolvedSegment[] => {
  return segments.filter((seg) => seg.value > 0).map((seg) => {
    const base = resolveColor(seg.color)
    return {
      key: seg.key,
      color: seg.filter ? applyFilterString(base, seg.filter) : base,
      value: seg.value,
    }
  })
}

const heightForCount = (count: number, width: number, squareSize: number, gap: number, widthScale = 1): number => {
  const perRow = squaresPerRow(width, squareSize, gap, widthScale)
  const rows = perRow > 0 ? Math.ceil(count / perRow) : 0
  return rows > 0 ? rows * squareSize + (rows - 1) * gap : 0
}

const tweenOptions = (
  onUpdate: () => void,
  durationMs: number,
  ease: [number, number, number, number],
) => ({
  type: "tween" as const,
  duration: durationMs / 1000,
  ease,
  onUpdate,
})

const createStateReader = () => ({
  resolvedSegments: [] as ResolvedSegment[],
  totalValue: 0,
  targetCount: 0,
  squareSize: 0,
  gap: 0,
  containerWidth: 0,
  devicePixelRatio: 1,
  rafId: null as number | null,
  lastCanvasHeight: 0,
  squareDitherColor: null as [number, number, number, number] | null,
  squareDitherCellPx: DEFAULT_DITHER_TILE_PX,
  squareDitherDotDensity: DEFAULT_DITHER_DOT_AREA_FRACTION,
  squareDitherDotDensityByKey: null as Partial<Record<SegmentKey, number>> | null,
  squareDitherOutlineColor: null as [number, number, number, number] | null,
  squareDitherOutlineWidthPx: 1,
  squareSolidColor: null as [number, number, number, number] | null,
  squareOutlineColor: null as [number, number, number, number] | null,
  squareOutlineWidthPx: 1,
  squareOutlineByKey: null as Partial<Record<SegmentKey, { color: [number, number, number, number]; widthPx: number; radiusPx?: number }>> | null,
  squareFillColorByKey: null as Partial<Record<SegmentKey, [number, number, number, number]>> | null,
  squareFillRadiusPx: null as number | null,
  dotMode: false,
  dotDiameterScaleByKey: null as Partial<Record<SegmentKey, number>> | null,
  widthScale: 1,
  justifyRows: false,
})

const parseRgba = (input: string): [number, number, number, number] => {
  const m = input.match(/rgba?\(([^)]+)\)/)
  if (!m) return [0, 0, 0, 1]
  const parts = m[1].split(",").map((s) => parseFloat(s.trim()))
  const [r = 0, g = 0, b = 0, a = 1] = parts
  return [r, g, b, isFinite(a) ? a : 1]
}

const clamp255 = (v: number): number => Math.max(0, Math.min(255, v))

// Emulates `filter: brightness(k) saturate(s)` for canvas fills. Functions are
// applied in the order they appear in the string (like CSS), so e.g.
// "saturate(.1) brightness(2.5)" desaturates toward gray first, then brightens.
// Each function clamps to [0, 255] like the browser's per-stage filter buffers,
// so brightness values that blow past 255 lose hue before any later saturate().
const applyFilterString = (
  color: [number, number, number, number],
  filter: string,
): [number, number, number, number] => {
  let [r, g, b] = color
  const a = color[3]
  const filterFunctionsInWrittenOrder = [...filter.matchAll(/(brightness|saturate)\(([0-9.]+)\)/g)]
  for (const [, functionName, rawAmount] of filterFunctionsInWrittenOrder) {
    const amount = parseFloat(rawAmount)
    if (!isFinite(amount)) continue
    if (functionName === "brightness") {
      r = clamp255(r * amount)
      g = clamp255(g * amount)
      b = clamp255(b * amount)
    } else {
      // CSS saturate(): lerp each channel toward the per-pixel grayscale value
      // using Rec. 709 luma (matches the browser's filter implementation closely
      // enough for our visual purposes).
      const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b
      r = clamp255(gray + (r - gray) * amount)
      g = clamp255(gray + (g - gray) * amount)
      b = clamp255(gray + (b - gray) * amount)
    }
  }
  return [r, g, b, a]
}
